<template>
    <!-- Mensajes: mismo diseño que Mi cuenta. Lateral con título, cifras,
         buscador y la lista de conversaciones; a la derecha la tarjeta del chat
         (cabecera, mensajes agrupados por día, aviso y caja para escribir).
         Misma API, mismo store y mismo Pusher que antes. -->
    <div class="bk bk-msg bk--full" :class="{ 'is-chat-open': abierta }" :style="{ '--bk-cookies': cookiesAlto + 'px' }">
        <aside class="bk-side bk-msg-side">
            <h1 class="bk-title">{{ $t('nuevo_menu.msg.title_a') }} <span class="bk-grad">{{ $t('nuevo_menu.msg.title_b') }}</span></h1>
            <p class="bk-subtitle">{{ $t('nuevo_menu.msg.sub') }}</p>

            <div class="bk-stats bk-stats--2">
                <div class="bk-stat bk-stat--ico">
                    <span class="bk-stat-ico" aria-hidden="true" v-html="ICON.msg"></span>
                    <span><span class="bk-stat-n">{{ conversations.length }}</span><span class="bk-stat-l">{{ $t('nuevo_menu.msg.stat_total') }}</span></span>
                </div>
                <div :class="['bk-stat', 'bk-stat--ico', 'bk-stat--unread', { 'is-on': sinLeer > 0 }]">
                    <span class="bk-stat-ico" aria-hidden="true" v-html="ICON.mail"></span>
                    <span><span class="bk-stat-n">{{ sinLeer }}</span><span class="bk-stat-l">{{ $t('nuevo_menu.msg.stat_unread') }}</span></span>
                </div>
            </div>

            <label v-if="conversations.length > 3" class="bk-search bk-msg-search">
                <span class="bk-sr-only">{{ $t('nuevo_menu.msg.search') }}</span>
                <span v-html="ICON.search"></span>
                <input v-model="busqueda" type="search" :placeholder="$t('nuevo_menu.msg.search')" autocomplete="off">
            </label>

            <div v-if="initialLoading" class="bk-loading-box bk-msg-loading"><HyFLoader variant="inline" /></div>

            <p v-else-if="!conversations.length" class="bk-msg-none">{{ $t('conversation.no-conversations') }}</p>

            <ul v-else class="bk-msg-list" :aria-label="$t('nuevo_menu.msg.list')">
                <li v-for="c in lista" :key="c.id">
                    <button
                        type="button"
                        :class="['bk-msg-item', { 'is-on': c.id === selectedConversationId, 'is-unread': c.unread }]"
                        :aria-current="c.id === selectedConversationId ? 'true' : null"
                        @click="selectConversation(c)"
                    >
                        <BkAvatar class="bk-ava--sm" :src="photoUrl(c.accommodation)" :nombre="otraPersona(c)" />
                        <span class="bk-msg-item-txt">
                            <span class="bk-msg-item-who">{{ otraPersona(c) }}</span>
                            <span class="bk-msg-item-place">{{ lugar(c) }}</span>
                            <span class="bk-msg-item-last">{{ c.last_message }}</span>
                        </span>
                        <span v-if="c.unread" class="bk-msg-dot"><span class="bk-sr-only">{{ $t('nuevo_menu.msg.unread') }}</span></span>
                    </button>
                </li>
                <li v-if="!lista.length" class="bk-msg-none">{{ $t('nuevo_menu.msg.no_match') }}</li>
            </ul>
        </aside>

        <div class="bk-main bk-msg-main">
            <!-- Cargando las conversaciones: mismo esqueleto que el chat, sin "no tienes". -->
            <div v-if="initialLoading" class="bk-card bk-chat bk-chat--wait" aria-hidden="true"></div>

            <!-- Sin conversación elegida -->
            <div v-else-if="!abierta && !fetchingMessages" class="bk-empty bk-msg-empty">
                <div class="bk-empty-ico" aria-hidden="true" v-html="ICON.msg"></div>
                <p class="bk-empty-title">{{ conversations.length ? $t('nuevo_menu.msg.pick') : $t('conversation.no-conversations') }}</p>
                <p class="bk-empty-text">{{ $t('nuevo_menu.msg.pick_text') }}</p>
            </div>

            <section v-else class="bk-card bk-chat" :aria-labelledby="'chat-title'">
                <header class="bk-chat-head">
                    <button v-if="isMobile" type="button" class="bk-ib" :aria-label="$t('conversation.back')" @click="deselectConversation" v-html="ICON.back"></button>
                    <BkAvatar v-if="selectedConversation" :src="photoUrl(selectedConversation.accommodation)" :nombre="otraPersona(selectedConversation)" grande />
                    <div class="bk-chat-head-txt">
                        <h2 id="chat-title" class="bk-card-title">{{ selectedConversation ? otraPersona(selectedConversation) : '…' }}</h2>
                        <p class="bk-card-desc">{{ selectedConversation ? lugar(selectedConversation) : '' }}</p>
                    </div>
                    <span v-if="selectedConversation" class="bk-badge bk-badge--plain">{{ $t(soyAnfitrion(selectedConversation) ? 'nuevo_menu.msg.as_host' : 'nuevo_menu.msg.as_guest') }}</span>
                </header>

                <div v-if="fetchingMessages" class="bk-loading-box bk-chat-loading"><HyFLoader variant="inline" local /></div>

                <div v-else ref="messagesContainer" class="bk-chat-msgs" role="log" aria-live="polite" :aria-label="$t('nuevo_menu.msg.history')">
                    <template v-for="g in grupos">
                        <p :key="'d' + g.dia" class="bk-chat-day"><span>{{ g.dia }}</span></p>
                        <div v-for="m in g.mensajes" :key="m.id" :class="['bk-bubble-row', messageClassName(m)]">
                            <div class="bk-bubble">
                                <!-- Texto escapado; solo los enlaces se convierten en <a>. -->
                                <p class="bk-bubble-txt" v-html="formatMessageContent(m)"></p>
                                <span class="bk-bubble-time">{{ hora(m.created_at) }}</span>
                            </div>
                        </div>
                    </template>
                    <p v-if="!messages.length" class="bk-msg-none">{{ $t('nuevo_menu.msg.no_messages') }}</p>
                </div>

                <p class="bk-chat-warn"><span v-html="ICON.lock"></span>{{ $t('conversation.contact-data-warning') }}</p>

                <div class="bk-chat-compose">
                    <div :class="['bk-chat-field', { 'is-invalid': !!errorMsg }]">
                        <label for="chat-msg" class="bk-sr-only">{{ $t('conversation.message') }}</label>
                        <textarea
                            id="chat-msg"
                            ref="caja"
                            v-model="formData.message"
                            :placeholder="$t('conversation.message-placeholder')"
                            rows="1"
                            maxlength="512"
                            :aria-invalid="errorMsg ? 'true' : 'false'"
                            aria-describedby="chat-msg-error chat-msg-hint"
                            @input="alEscribir"
                            @keydown.enter.exact.prevent="handleSubmit"
                        ></textarea>
                        <button type="button" class="bk-btn bk-btn--primary bk-chat-send" :disabled="loading || !formData.message.trim()" :aria-label="$t('conversation.send')" @click="handleSubmit">
                            <span v-if="loading" class="bk-spinner" aria-hidden="true"></span>
                            <span v-else v-html="ICON.send"></span>
                            <span class="bk-chat-send-txt">{{ $t('conversation.send') }}</span>
                        </button>
                    </div>
                    <div class="bk-chat-foot">
                        <p id="chat-msg-error" class="bk-chat-error" role="alert">{{ errorMsg }}</p>
                        <p id="chat-msg-hint" class="bk-chat-hint">{{ $t('nuevo_menu.msg.hint') }} · {{ formData.message.length }}/512</p>
                    </div>
                </div>
            </section>
        </div>

        <div v-if="aviso" class="bk-toast-fixed">
            <div :class="['bk-toast', `bk-toast--${aviso.tipo}`]" role="alert">
                <span class="bk-toast-icon" aria-hidden="true">!</span>
                <p class="bk-toast-text">{{ aviso.texto }}</p>
                <button type="button" class="bk-toast-close" :aria-label="$t('nuevo_menu.close')" @click="aviso = null">×</button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { initializePusher, subscribeToChannel, unsubscribeFromChannel } from "@/Utils/PusherUtils";
import HyFLoader from "@/components/Loader/HyFLoader.vue";
import BkAvatar from "@/components/Cuenta/BkAvatar.vue";
import deviceMixin from "@/mixins/deviceMixin";
import ICON from "@/components/Cuenta/iconos";

// Canal privado del chat (ver BroadcastAuthController en la API).
const canalDe = id => `private-conversation.${id}`;

const LOCALES = { es: 'es-CL', en: 'en-US', pt: 'pt-BR', fr: 'fr-FR', it: 'it-IT', de: 'de-DE' };
const escapar = t => String(t).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

export default {
    name: "Conversation",
    mixins: [deviceMixin],
    components: { HyFLoader, BkAvatar },
    data() {
        return {
            ICON,
            url: process.env.VUE_APP_S3_URL,
            selectedConversationId: null,
            formData: { message: '' },
            loading: false,
            fetchingMessages: false,
            initialLoading: true,
            busqueda: '',
            errorMsg: '',
            aviso: null,
            avisoTimer: null,
            // Alto del aviso de cookies (fijo abajo): el chat se achica para no quedar tapado.
            cookiesAlto: 0,
        };
    },
    computed: {
        ...mapGetters("conversations", ["conversations", "messages", "selectedConversation"]),
        userId() {
            return parseInt(this.$store.state.user.idusuario, 10);
        },
        abierta() {
            return this.selectedConversationId !== null;
        },
        sinLeer() {
            return this.conversations.filter(c => c.unread).length;
        },
        // No leídas primero (como antes), después el resto; filtradas por la búsqueda.
        lista() {
            const q = this.busqueda.trim().toLowerCase();
            const todas = [...this.conversations.filter(c => c.unread), ...this.conversations.filter(c => !c.unread)];
            return q ? todas.filter(c => `${c.title} ${c.last_message || ''}`.toLowerCase().includes(q)) : todas;
        },
        // Mensajes agrupados por día ("lunes, 4 de octubre").
        grupos() {
            const loc = LOCALES[this.$i18n.locale] || 'es';
            const out = [];
            for (const m of this.messages || []) {
                const d = new Date(m.created_at);
                let dia = isNaN(d) ? '' : new Intl.DateTimeFormat(loc, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(d);
                dia = dia.charAt(0).toUpperCase() + dia.slice(1);
                if (!out.length || out[out.length - 1].dia !== dia) out.push({ dia, mensajes: [] });
                out[out.length - 1].mensajes.push(m);
            }
            return out;
        },
    },
    methods: {
        ...mapActions("conversations", ["fetchConversations", "fetchMessages", "sendMessage"]),

        avisar(texto) {
            clearTimeout(this.avisoTimer);
            this.aviso = { tipo: 'error', texto };
            this.avisoTimer = setTimeout(() => { this.aviso = null; }, 6000);
        },
        soyAnfitrion(c) {
            return Number(c.host_id) === this.userId;
        },
        // El título viene "Alojamiento - Persona": se separan para mostrarlos en dos líneas.
        partes(c) {
            const t = String(c.title || '');
            const i = t.lastIndexOf(' - ');
            return i > 0 ? { lugar: t.slice(0, i).replace(/\s+/g, ' ').trim(), persona: t.slice(i + 3).trim() } : { lugar: t, persona: '' };
        },
        otraPersona(c) {
            return this.partes(c).persona || this.partes(c).lugar;
        },
        lugar(c) {
            const p = this.partes(c);
            return p.persona ? p.lugar : (c.accommodation && c.accommodation.title) || '';
        },

        async selectConversation(conversation) {
            if (conversation.id === this.selectedConversationId && !this.fetchingMessages) return;
            this.fetchingMessages = true;
            this.errorMsg = '';
            if (this.selectedConversationId) unsubscribeFromChannel(canalDe(this.selectedConversationId));
            this.selectedConversationId = conversation.id;
            // Mientras está abierta, sus mensajes nuevos no suenan ni avisan.
            this.$store.commit('notificaciones/setConversacionAbierta', conversation.id);
            this.$store.dispatch('notificaciones/leerEntidad', { entidad: 'conversacion', id: conversation.id });
            if (String(this.$route.query.c || '') !== String(conversation.id)) {
                this.$router.replace({ query: { ...this.$route.query, c: String(conversation.id) } }).catch(() => {});
            }
            try {
                await this.fetchMessages(this.selectedConversationId);
                if (conversation.unread) conversation.unread = false;
                this.fetchingMessages = false;
                this.scrollToBottom();
                // Canal privado: lo firma la API solo para el anfitrión y el huésped.
                subscribeToChannel(canalDe(this.selectedConversationId), "MessageConversationSent", (data) => {
                    this.handleNewMessage(data.message);
                    this.scrollToBottom();
                });
                if (this.isMobile) window.scrollTo(0, 0);
            } catch (error) {
                console.error("Error fetching messages:", error);
                this.fetchingMessages = false;
                this.avisar(this.$t('nuevo_menu.msg.load_error'));
            }
        },

        handleNewMessage(message) {
            try {
                this.messages.push(message);
                const i = this.conversations.findIndex(conv => conv.id === message.conversation_id);
                if (i !== -1) {
                    const conversation = this.conversations[i];
                    this.$set(conversation, 'last_message', message.content || message.message);
                    if (conversation.id !== this.selectedConversationId) this.$set(conversation, 'unread', true);
                    this.conversations.splice(i, 1);
                    this.conversations.unshift(conversation);
                }
            } catch (error) {
                console.error("Error al manejar el nuevo mensaje:", error);
            }
        },

        // Mismas reglas que antes: 2 a 512 caracteres y sin teléfono ni correo.
        validar(text) {
            if (text.length < 2) return this.$t('nuevo_menu.msg.too_short');
            if (text.length > 512) return this.$t('nuevo_menu.msg.too_long');
            if (this.containsContactData(text)) return this.$t('nuevo_menu.msg.contact');
            return '';
        },
        alEscribir() {
            if (this.errorMsg) this.errorMsg = '';
            this.ajustarAlto();
        },
        ajustarAlto() {
            const t = this.$refs.caja;
            if (!t) return;
            t.style.height = 'auto';
            t.style.height = Math.min(t.scrollHeight, 180) + 'px';
        },

        async handleSubmit() {
            if (this.loading) return;
            const text = (this.formData.message || '').trim();
            const error = this.validar(text);
            if (error) {
                this.errorMsg = error;
                this.$nextTick(() => this.$refs.caja && this.$refs.caja.focus());
                return;
            }
            const conv = this.selectedConversation;
            if (!conv || (this.userId !== Number(conv.host_id) && this.userId !== Number(conv.guest_id))) {
                console.error('Sender does not belong to this conversation');
                return;
            }
            this.loading = true;
            try {
                const response = await this.sendMessage({ conversationId: this.selectedConversationId, message: text, senderRole: this.soyAnfitrion(conv) ? 'host' : 'guest' });
                // La acción del store traga el error y devuelve undefined.
                if (!response) throw new Error('sin respuesta');
                const i = this.conversations.findIndex(c => c.id === this.selectedConversationId);
                if (i !== -1) {
                    const activa = this.conversations[i];
                    this.$set(activa, 'last_message', text);
                    this.$set(activa, 'unread', false);
                    this.conversations.splice(i, 1);
                    this.conversations.unshift(activa);
                }
                this.formData.message = '';
                this.$nextTick(this.ajustarAlto);
            } catch (error) {
                console.error("Error sending message:", error);
                this.avisar(this.$t('nuevo_menu.msg.send_error'));
            } finally {
                this.loading = false;
            }
        },
        scrollToBottom() {
            this.$nextTick(() => {
                const c = this.$refs.messagesContainer;
                if (c) c.scrollTop = c.scrollHeight;
            });
        },
        hora(date) {
            const d = new Date(date);
            return isNaN(d) ? '' : new Intl.DateTimeFormat(LOCALES[this.$i18n.locale] || 'es', { hour: '2-digit', minute: '2-digit' }).format(d);
        },
        messageClassName(message) {
            return this.userId == message.sender_id ? 'sent' : 'received';
        },
        // Foto del alojamiento de la conversación, o '' (entonces va la inicial).
        photoUrl(accommodation) {
            const photo = accommodation && accommodation.photos && accommodation.photos[0];
            if (!photo || !photo.url) return '';
            return photo.url.indexOf('http') > -1 ? photo.url : this.url + photo.url;
        },
        deselectConversation() {
            if (this.selectedConversationId) unsubscribeFromChannel(canalDe(this.selectedConversationId));
            this.selectedConversationId = null;
            this.$store.commit('notificaciones/setConversacionAbierta', null);
            if (this.$route.query.c) {
                const q = { ...this.$route.query };
                delete q.c;
                this.$router.replace({ query: q }).catch(() => {});
            }
        },
        // Aviso de la campana de un mensaje de OTRA conversación: se sube en la
        // lista y queda como no leída (los de la abierta llegan por su canal).
        alNotificar(e) {
            const n = e && e.detail;
            if (!n || n.tipo !== 'mensaje' || !n.datos) return;
            const id = Number(n.datos.conversacion_id);
            if (id === Number(this.selectedConversationId)) return;
            const i = this.conversations.findIndex(c => Number(c.id) === id);
            if (i === -1) return;
            const c = this.conversations[i];
            this.$set(c, 'last_message', n.datos.extracto || c.last_message);
            this.$set(c, 'unread', true);
            this.conversations.splice(i, 1);
            this.conversations.unshift(c);
        },
        formatMessageContent(message) {
            const texto = typeof message === 'string' ? message : message.message || '';
            // Primero se escapa todo (un mensaje no puede inyectar HTML) y después
            // se convierten los enlaces. Los saltos de línea se respetan con CSS.
            return escapar(texto).replace(/(https?:\/\/[^\s,<]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');
        },
        containsContactData(text) {
            if (!text) return false;
            const emailRegex = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
            const normalized = text.replace(/[\s\-().]/g, '');
            return emailRegex.test(text) || /\d{9,}/.test(normalized);
        },
    },
    async created() {
        try {
            initializePusher();
            await this.fetchConversations();
            // ?c=ID abre esa conversación (enlace de la campana); si no, la primera.
            const pedida = this.conversations.find(c => String(c.id) === String(this.$route.query.c || ''));
            if (pedida) {
                await this.selectConversation(pedida);
            } else if (this.conversations.length > 0 && !this.isMobile) {
                await this.selectConversation(this.lista[0]);
            }
        } catch (error) {
            console.error("Error loading conversations:", error);
            this.avisar(this.$t('nuevo_menu.msg.load_error'));
        } finally {
            this.initialLoading = false;
        }
    },
    watch: {
        // La campana lleva a /conversation?c=ID estando ya en Mensajes.
        '$route.query.c'(c) {
            if (!c || String(c) === String(this.selectedConversationId)) return;
            const conv = this.conversations.find(x => String(x.id) === String(c));
            if (conv) this.selectConversation(conv);
        },
    },
    mounted() {
        const medir = () => {
            const b = document.querySelector('.cookie-footer');
            this.cookiesAlto = b ? Math.round(b.getBoundingClientRect().height) : 0;
        };
        medir();
        const app = document.getElementById('page');
        if (app && typeof MutationObserver !== 'undefined') {
            this.cookiesMO = new MutationObserver(medir);
            this.cookiesMO.observe(app, { childList: true });
        }
        window.addEventListener('resize', medir);
        this.medirCookies = medir;
        window.addEventListener('hyf:notificacion', this.alNotificar);
    },
    beforeDestroy() {
        if (this.cookiesMO) this.cookiesMO.disconnect();
        window.removeEventListener('resize', this.medirCookies);
        window.removeEventListener('hyf:notificacion', this.alNotificar);
        clearTimeout(this.avisoTimer);
        if (this.selectedConversationId) unsubscribeFromChannel(canalDe(this.selectedConversationId));
        this.$store.commit('notificaciones/setConversacionAbierta', null);
        // Sin disconnectPusher(): la conexión la comparten las notificaciones del header.
    },
};
</script>

<style src="../../components/BankInformation/bkCuenta.css"></style>
<style src="../../components/Cuenta/cuentaMenu.css"></style>
<style>
/* ══ Mensajes ══════════════════════════════════════════════════════ */
.bk-msg :where(p, ul, li, h2) { margin: 0; padding: 0; list-style: none; }
.bk-msg .bk-search > span:not(.bk-sr-only), .bk-chat-warn > span, .bk-chat-send > span:not(.bk-chat-send-txt):not(.bk-spinner) { display: contents; }
.bk-stats--2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.bk-msg-search { margin: 18px 0 0 !important; flex: none; }
.bk-msg-loading { min-height: 160px; }

/* Lateral más ancho que en las otras páginas: la lista de conversaciones necesita lugar.
   La página sube (menos aire arriba) y deja espacio antes del footer. */
.bk.bk-msg { padding-bottom: 64px; }
@media (min-width: 1024px) {
    .bk.bk-msg {
        --bk-top: 96px;
        grid-template-columns: minmax(340px, 420px) minmax(0, 1fr); gap: 40px;
        margin-top: -36px; padding-top: 0;
    }
    .bk-msg .bk-msg-main { padding-top: 24px; }
    /* El lateral ocupa la altura de la pantalla y la lista llena lo que queda (con su scroll). */
    .bk-msg-side { height: calc(100vh - var(--bk-top) - 24px - var(--bk-cookies, 0px)); min-height: 560px; padding-bottom: 0; }
    .bk-msg-list { flex: 1 1 auto; min-height: 0; overflow-y: auto; overflow-x: hidden; }
}
.bk-msg-side { display: flex; flex-direction: column; }
.bk-msg-side .bk-title { font-size: clamp(32px, 3vw, 40px); }

/* Cifras más grandes, con ícono */
.bk-msg .bk-stats { gap: 10px; margin-top: 18px; }
.bk-msg .bk-subtitle { font-size: 15px; line-height: 1.5; }
.bk-msg-search { margin: 14px 0 0 !important; }
.bk-msg-search input { height: 44px; }
.bk-stat--ico { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border-radius: 18px; background: #fff; box-shadow: 0 12px 30px -20px rgba(122, 57, 0, .45); }
.bk-stat--ico > span:last-child { display: flex; flex-direction: column; min-width: 0; text-align: left; }
.bk-stat-ico { display: grid; place-items: center; flex: none; width: 44px; height: 44px; border-radius: 14px; background: var(--bk-orange-50); color: var(--bk-orange-dark); }
.bk-stat-ico svg { width: 22px; height: 22px; }
.bk-stat--ico .bk-stat-n { font-size: 28px; line-height: 1; }
.bk-stat--ico .bk-stat-l { margin-top: 4px; font-size: 13.5px; font-weight: 500; }
.bk-stat--unread .bk-stat-ico { background: #f6f2ee; color: var(--bk-muted); }
.bk-stat--unread.is-on { border-color: rgba(232, 100, 10, .25); }
.bk-stat--unread.is-on .bk-stat-ico { background: var(--bk-grad-d); color: #fff; }
.bk-stat--unread.is-on .bk-stat-n { color: var(--bk-magenta); }

/* Lista */
.bk-msg-list {
    display: flex; flex-direction: column; gap: 6px; margin-top: 16px !important;
    padding: 4px 6px 4px 2px !important; scrollbar-width: thin;
}
.bk-msg-item {
    position: relative; display: grid; grid-template-columns: 46px minmax(0, 1fr) auto; gap: 12px; align-items: center;
    width: 100%; padding: 10px 12px; margin: 0; border: 1px solid rgba(29, 26, 23, .05); border-radius: 18px; background: rgba(255, 255, 255, .6);
    font: inherit; text-align: left; color: var(--bk-ink); cursor: pointer; transition: background .15s, border-color .15s, box-shadow .15s, transform .15s;
}
.bk-msg-item:hover { background: #fff; border-color: rgba(29, 26, 23, .1); }
.bk-msg-item:focus { outline: none; }
.bk-msg-item:focus-visible { outline: 2px solid var(--bk-orange); outline-offset: 2px; }
.bk-msg-item.is-on { background: #fff; border-color: rgba(255, 122, 0, .35); box-shadow: 0 12px 28px -16px rgba(122, 57, 0, .5); transform: none; }
.bk-msg-item.is-on::before { content: ''; position: absolute; left: -1px; top: 14px; bottom: 14px; width: 4px; border-radius: 4px; background: var(--bk-grad-d); }
.bk-msg-item-txt { display: flex; flex-direction: column; min-width: 0; }
.bk-msg-item-who { font-size: 16px; font-weight: 700; line-height: 1.3; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bk-msg-item-place { font-size: 13px; color: var(--bk-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bk-msg-item-last { margin-top: 3px; font-size: 14px; color: var(--bk-ink-2); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bk-msg-item.is-unread { background: #fff; border-color: rgba(232, 100, 10, .2); }
.bk-msg-item.is-unread .bk-msg-item-who, .bk-msg-item.is-unread .bk-msg-item-last { font-weight: 700; color: var(--bk-ink); }
.bk-ava--sm { width: 46px; height: 46px; font-size: 16px; }
.bk-msg-dot { width: 11px; height: 11px; border-radius: 50%; background: var(--bk-magenta); box-shadow: 0 0 0 4px rgba(232, 100, 10, .15); }
.bk-msg-none { padding: 14px 4px; font-size: 14px; color: var(--bk-muted); }

/* Chat */
.bk-msg-main { min-width: 0; }
.bk-chat--wait { background: linear-gradient(100deg, rgba(255,255,255,.7) 40%, rgba(255,247,239,.95) 50%, rgba(255,255,255,.7) 60%) 0 0 / 200% 100%; animation: bk-shimmer 1.4s linear infinite; }
@keyframes bk-shimmer { to { background-position: -200% 0; } }
.bk-msg-empty { margin-top: 0; }
/* Alto: pantalla menos el header y su aire, el aire de abajo y la barra de cookies. */
.bk-chat { display: flex; flex-direction: column; padding: 0 !important; overflow: hidden; height: calc(100vh - 96px - 48px - var(--bk-cookies, 0px)); min-height: 520px; }
.bk-chat-head { display: flex; align-items: center; gap: 14px; padding: 18px 22px; border-bottom: 1px solid var(--bk-line); }
.bk-chat-head-txt { flex: 1; min-width: 0; }
.bk-chat-head .bk-card-title { margin: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bk-chat-head .bk-card-desc { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bk-chat-loading { flex: 1; min-height: 0; }
.bk-chat-msgs {
    flex: 1; min-height: 0; overflow-y: auto; padding: 18px 22px; display: flex; flex-direction: column; gap: 6px;
    background:
        radial-gradient(60% 40% at 100% 0%, rgba(255, 122, 0, .05), transparent 70%),
        radial-gradient(60% 40% at 0% 100%, rgba(109, 60, 224, .05), transparent 70%);
}
.bk-chat-day { display: flex; justify-content: center; margin: 10px 0 6px !important; position: sticky; top: 0; z-index: 1; }
.bk-chat-day span { padding: 4px 12px; border-radius: 999px; background: rgba(255, 255, 255, .92); border: 1px solid var(--bk-line); font-size: 12px; font-weight: 600; color: var(--bk-muted); }
.bk-bubble-row { display: flex; }
.bk-bubble-row.sent { justify-content: flex-end; }
.bk-bubble {
    max-width: min(78%, 560px); padding: 10px 14px 8px; border-radius: 18px 18px 18px 6px;
    background: #fff; border: 1px solid var(--bk-line); box-shadow: 0 6px 16px -12px rgba(29, 26, 23, .3);
    animation: bk-rise .25s var(--bk-ease) both;
}
.bk-bubble-row.sent .bk-bubble { border-radius: 18px 18px 6px 18px; border-color: transparent; background: var(--bk-grad-d); color: #fff; box-shadow: 0 8px 18px -12px rgba(232, 100, 10, .7); }
.bk-bubble-txt { font-size: 15px; line-height: 1.5; white-space: pre-wrap; overflow-wrap: anywhere; }
.bk-bubble-txt a { color: inherit; text-decoration: underline; text-underline-offset: 2px; }
.bk-bubble-time { display: block; margin-top: 4px; font-size: 11.5px; text-align: right; color: var(--bk-muted); }
.bk-bubble-row.sent .bk-bubble-time { color: rgba(255, 255, 255, .8); }

.bk-chat-warn { display: flex; gap: 8px; align-items: flex-start; padding: 10px 22px; background: var(--bk-amber-bg); border-top: 1px solid rgba(183, 121, 31, .2); font-size: 13px; line-height: 1.45; color: #7a5212; }
.bk-chat-warn svg { flex: none; width: 16px; height: 16px; margin-top: 1px; }
.bk-chat-compose { padding: 14px 18px 12px; border-top: 1px solid var(--bk-line); background: #fff; }
.bk-chat-field { display: flex; align-items: flex-end; gap: 10px; padding: 6px 6px 6px 16px; border: 1.5px solid var(--bk-line-2); border-radius: 22px; background: #fff; transition: border-color .2s, box-shadow .2s; }
.bk-chat-field:focus-within { border-color: var(--bk-orange); box-shadow: var(--bk-ring); }
.bk-chat-field.is-invalid { border-color: var(--bk-danger); }
.bk-chat-field textarea {
    flex: 1; min-height: 42px; max-height: 180px; padding: 10px 0; margin: 0; border: 0; outline: none; resize: none; background: none;
    font: inherit; font-size: 16px; line-height: 1.4; color: var(--bk-ink);
}
.bk-chat-field textarea:focus, .bk-chat-field textarea:focus-visible { outline: none; box-shadow: none; }
.bk-chat-send { min-height: 44px; padding: 0 18px; flex: none; }
.bk-chat-send svg { width: 18px; height: 18px; }
.bk-chat-foot { display: flex; justify-content: space-between; gap: 10px; margin-top: 6px; padding: 0 6px; }
.bk-chat-error { font-size: 13px; font-weight: 500; color: var(--bk-danger); }
.bk-chat-hint { margin-left: auto !important; font-size: 12px; color: var(--bk-muted); white-space: nowrap; }

@media (max-width: 1023px) {
    .bk-msg-list { max-height: none; min-height: 0; overflow: visible; text-align: left; }
    .bk-msg-side { text-align: center; }
    .bk-msg-search, .bk-msg-list { width: 100%; max-width: 560px; margin-left: auto !important; margin-right: auto !important; }
    .bk-msg-main { padding-top: 0; }
    /* Con un chat abierto en pantallas chicas se ve solo el chat. */
    .bk-msg.is-chat-open .bk-msg-side { display: none; }
    .bk-msg:not(.is-chat-open) .bk-msg-main { display: none; }
    .bk-chat { height: calc(100vh - 110px - var(--bk-cookies, 0px)); height: calc(100dvh - 110px - var(--bk-cookies, 0px)); min-height: 420px; margin-top: 14px; }
}
@media (max-width: 640px) {
    .bk-chat-head, .bk-chat-msgs { padding-left: 14px; padding-right: 14px; }
    .bk-chat-warn { padding: 8px 14px; font-size: 12.5px; }
    .bk-chat-compose { padding: 10px 10px 8px; }
    .bk-chat-send-txt { display: none; }
    .bk-chat-send { width: 44px; padding: 0; }
    .bk-bubble { max-width: 88%; }
    /* El rol ocupa mucho al lado del nombre: en celular no se muestra. */
    .bk-chat-head { gap: 10px; }
    .bk-chat-head .bk-badge { display: none; }
}
</style>
